import React, { useState, useMemo, useCallback } from 'react';
import './MelODGen.css';
import Slider from './MelODGen_Slider';
import Selector from './MelODGen_Selector';
import ReactSlider from 'react-slider';
import Instruments from './MelODGen_Instruments';
import scaleGenerator from './MelODGen_scaleGenerator';
import SequenceGenerator from './MelODGen_GenSequence';
import MelodyChart from './MelODGen_MelodyChart';
import GenerateMIDIFiles from './MelODGen_Midigen';
import MelodyPlayer from './MelODGen_MelodyPlayer';

function MelODGen(colorModeClass) {

    //
    //                              I
    //                    Variable Declarations
    //
    // eslint-disable-next-line
    const noteSizes = [ '1/2', '1/4', '1/8', '1/16', '1/32' ];
    const noteSizesValues = useMemo(() => [ 0.5, 0.25, 0.125, 0.0625, 0.03125 ], []);
    const keys = useMemo(() => [ 'C', 'C#/Db', 'D', 'D#/Eb', 'E', 'F', 'F#/Gb', 'G', 'G#/Ab', 'A', 'A#/Bb', 'B' ], []);
    const scales = useMemo(() => [ 'Ionian', 'Dorian', 'Phrygian', 'Lydian', 'Mixolydian', 'Aeolian', 'Locrian', 'Minor Pentatonic', 'Major Pentatonic', 'Minor Blues', 'Major Blues', 'Diminished', 'Whole Tone', 'Chromatic' ], []);
    //  User Selected Parameters:
    const [ userMeasures, setUserMeasures ] = useState(4);
    const [ userTempo, setUserTempo ] = useState(160);
    const [ userBeatsPerMeasure, setUserBeatsPerMeasure ] = useState(4);
    const [ userBeatSize, setUserBeatSize ] = useState(noteSizes[1]);
    const [ userNoteSize, setUserNoteSize ] = useState(noteSizes[3]);
    const [ userKey, setUserKey ] = useState(keys[0]);
    const [ userScale, setUserScale ] = useState(scales[0]);
    const [ userLowestMIDINote, setUserLowestMIDINote ] = useState(21);
    const [ userHighestMIDINote, setUserHighestMIDINote ] = useState(108);
    const [ userBusyness, setUserBusyness ] = useState(50);
    const [ collection, setCollection ] = useState([]);

    //  Some calculated values based on user inputs:
    const userBeatSizePosition = useMemo(() => noteSizes.indexOf(userBeatSize), [ noteSizes, userBeatSize]);
    const tonicChromatic = useMemo(() => keys.indexOf(userKey), [ keys, userKey ]);
    const octaveScale = useMemo(() => scaleGenerator(tonicChromatic, scales, userScale),[ tonicChromatic, scales, userScale]);
    const lowestNote = useMemo(() => keys[userLowestMIDINote % 12] + Math.floor(userLowestMIDINote / 12), [userLowestMIDINote, keys]);
    const highestNote = useMemo(() => keys[userHighestMIDINote % 12] + Math.floor(userHighestMIDINote / 12), [userHighestMIDINote, keys]);
    const beatCount = useMemo(() => userMeasures * userBeatsPerMeasure * noteSizesValues[userBeatSizePosition], [userMeasures, userBeatsPerMeasure, noteSizesValues, userBeatSizePosition]);

    //
    //                              III
    //                           Functions
    //
    //        Generate a random number between a min and a max.
    //
    const MidiRange = useCallback(() => {
        return (
        <div className="doubleSlider">              
            <p>Choose the MIDI range for the notes in the sequence:</p><p>  {lowestNote} - {highestNote}</p>
            <ReactSlider
            className="horizontal-slider"
            thumbClassName="thumb"
            trackClassName="track"
            defaultValue={[userLowestMIDINote, userHighestMIDINote]}
            ariaLabel={['Lower MIDI Note', 'Upper MIDI Note']}
            ariaValuetext={state => `Thumb value ${state.valueNow}`}
            renderThumb={({key, ...restProps}, state) => <div key={key} {...restProps}>{state.valueNow}</div>}
            pearling
            min={0}
            max={127}
            marks={[0, 33, 66, 99, 132]}
            minDistance={12}
            withTracks={true}
            onChange={(selection) => {
                setUserLowestMIDINote(selection[0]);
                setUserHighestMIDINote(selection[1])
            }}
            />
        </div>
        )

    }, [userLowestMIDINote, userHighestMIDINote, lowestNote, highestNote]);
  
    const BuildCharts = () => {
        setCollection(previousSequences => [
            ...previousSequences,
            SequenceGenerator(
                beatCount,
                tonicChromatic, 
                octaveScale, 
                userLowestMIDINote, 
                userHighestMIDINote, 
                userBusyness
            )
        ])
    };

    const [ playLastFile, setPlayLastFile ] = useState(false);

    //
    //                              IV
    //                            Output
    //
    return (
        <div className={colorModeClass}>
            <div>
                <div className="panel">
                    <h1>Random MIDI Melody Generator</h1>
                </div>
                <div className="panel">
                    <Instruments {...{ setUserLowestMIDINote, setUserHighestMIDINote }}/>
                </div>
                <div className="panel">
                    <div className="sliders">
                        <Slider label="Number of Measures:  " userValue={userMeasures} setUserValue={setUserMeasures} min={1} max={32} />
                        <Slider label="Tempo:  " userValue={userTempo} setUserValue={setUserTempo} min={1} max={240} />
                        <Slider label="Number of Beats Per Measures:  " userValue={userBeatsPerMeasure} setUserValue={setUserBeatsPerMeasure} min={2} max={17} />
                        <MidiRange />
                        <Slider label="Probability of an Event Being A Note:  " userValue={userBusyness} setUserValue={setUserBusyness} min={1} max={100} />
                    </div>
                    <div className="pick_list">
                        <div className="pick_item">
                        <p>
                            <span>Note that counts as a beat:  </span>
                            <Selector 
                                items={noteSizes} 
                                currentSelection={noteSizes.findIndex((item) => item === userBeatSize)} 
                                onSelect={(selectedItem) => setUserBeatSize(selectedItem)}
                            />
                        </p>
                        </div>
                        <div className="pick_item">
                        <p>
                            <span>Note size:   </span> 
                            <Selector 
                                items={noteSizes} 
                                currentSelection={noteSizes.findIndex((item) => item === userNoteSize)}
                                onSelect={(selectedItem) => setUserNoteSize(selectedItem)}
                            />
                        </p>
                        </div>
                        <div className="pick_item">
                        <p>
                            <span>Key:   </span> 
                            <Selector 
                                items={keys} 
                                currentSelection={keys.findIndex((item) => item === userKey)}
                                onSelect={(selectedItem) => setUserKey(selectedItem)}
                            />
                        </p>
                        </div>
                        <div className="pick_item">
                        <p>
                            <span>Mode or scale:   </span> 
                            <Selector
                                items={scales} 
                                currentSelection={scales.findIndex((item) => item === userScale)}
                                onSelect={(selectedItem) => setUserScale(selectedItem)}
                            />
                        </p>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <button className='midiButtons' onClick={BuildCharts}>Generate Charts</button>
            </div>
            <div className='melodyCharts'>
                {collection.map((sequence, index) => (<div key={index}>
                    <MelodyChart {...{sequence:sequence.sequence,number:index}} />
                    {(collection.length !== 0) && <GenerateMIDIFiles sequence={sequence} beatSize={noteSizesValues[userBeatSizePosition]} index={index}/>}
                </div>))}
            </div>
            <div>
                <button className='midiButtons' onClick={() => setPlayLastFile(true)}>Play Last Sequence</button>
                <button className='midiButtons' onClick={() => setPlayLastFile(false)}>Stop Playing</button>
            </div>
            <div>
                {(playLastFile) && <MelodyPlayer sequence={collection[collection.length - 1].sequence} tempo={userTempo} notePerBeat={noteSizesValues[userBeatSizePosition]} />}
            </div>
        </div>
    );
}

export default MelODGen;